<template>
  <div @keyup.esc="$set($store.state.show, 'terminal', false)" class="ai-agent-container" style="display: flex; flex-direction: column; height: 100vh; overflow: hidden;">
    <!-- Top Conversations Bar -->
    <div class="tasks-bar">
      <div class="task-tabs" v-if="$store.state.msgs">
        <button @click="$store.dispatch('agent/createConversation')" class="add-task-btn xs-font">+</button>
        <button
          v-for="msgId in Object.keys($store.state.msgs).filter(id => $store.state.msgs[id] && ($store.state.msgs[id].type === 'ai' || $store.state.msgs[id].type === 'ai-chat')).sort((a, b) => {
            const aTime = $store.state.msgs[a]?.created || 0
            const bTime = $store.state.msgs[b]?.created || 0
            return bTime - aTime
          })"
          :key="`tab-${msgId}-${$store.state.terminal.tabKey || 0}`"
          @click="$store.commit('set', {keys: ['terminal', 'currentConversationId'], val: msgId}); $store.commit('set', {keys: ['terminal', 'tabKey'], val: ($store.state.terminal.tabKey || 0) + 1}); $store.commit('set', {keys: ['terminal', 'historyKey'], val: ($store.state.terminal.historyKey || 0) + 1}); $store.commit('set', {keys: ['terminal', 'planKey'], val: ($store.state.terminal.planKey || 0) + 1}); $store.commit('set', {keys: ['terminal', 'contextKey'], val: ($store.state.terminal.contextKey || 0) + 1})"
          :class="['xs-font task-tab', {active: $store.state.terminal.currentConversationId === msgId}]"
        >
          {{ $store.state.msgs[msgId] ? $store.state.msgs[msgId].topic : msgId }}
        </button>
      </div>
    </div>

    <!-- Main Three-Column Layout -->
    <div class="main-layout" style="flex: 1; min-height: 0; overflow: hidden;">
      <!-- Left Column: User Prompt History -->
      <div class="left-panel sm-font" style="display: flex; flex-direction: column; overflow: hidden;">
        <div class="panel-header">Prompt History</div>
        <div class="history-list" style="flex: 1; overflow-y: auto; min-height: 0;" :key="`history-${$store.state.terminal.currentConversationId}-${$store.state.terminal.historyKey || 0}`">
          <template v-if="userPrompts.length > 0">
            <div
              v-for="(msg, index) in userPrompts"
              :key="`${msg.id || msg.timestamp || index}-${$store.state.terminal.currentConversationId}-${$store.state.terminal.historyKey || 0}`"
              @click="loadPlanFromHistory(msg); $store.commit('set', {keys: ['terminal', 'historyKey'], val: ($store.state.terminal.historyKey || 0) + 1}); $store.commit('set', {keys: ['terminal', 'planKey'], val: ($store.state.terminal.planKey || 0) + 1}); $store.commit('set', {keys: ['terminal', 'contextKey'], val: ($store.state.terminal.contextKey || 0) + 1})"
              :class="['history-item', {active: $store.state.terminal.selectedPrompt === msg}]"
            >
              <div class="history-prompt">{{ msg.text || msg.message || msg.content }}</div>
              <div class="history-response" v-if="getResponseForPrompt(msg)">
                <div class="response-intent">{{ getResponseForPrompt(msg).intent }}</div>
                <div class="response-components" v-if="getResponseForPrompt(msg).recommended_components && getResponseForPrompt(msg).recommended_components.length > 0">
                  <span v-for="comp in getResponseForPrompt(msg).recommended_components" :key="comp" class="component-tag">{{ comp }}</span>
                </div>
              </div>
              <div class="history-time">{{ new Date(parseInt(msg.id || msg.timestamp || Date.now())).toLocaleTimeString() }}</div>
            </div>
          </template>
          <div v-else class="history-empty">
            No prompts yet. Send a message to get started.
          </div>
        </div>
      </div>

      <!-- Center Column: AI Response Display -->
      <div class="center-panel">
        <template v-if="$store.state.terminal.lastPlan">
          <div class="plan-area" :key="`plan-${$store.state.terminal.currentConversationId}-${$store.state.terminal.planKey || 0}`">
            <!-- Phase indicator -->
            <div class="phase-indicator" v-if="$store.state.terminal.lastPlan.phase">
              <span class="phase-badge">{{ $store.state.terminal.lastPlan.phase.toUpperCase() }}</span>
            </div>
            
            <div class="plan-summary">
              <div class="plan-text" v-if="$store.state.terminal.lastPlan.plan">{{ $store.state.terminal.lastPlan.plan }}</div>
              <div class="plan-text" v-if="$store.state.terminal.lastPlan.message">{{ $store.state.terminal.lastPlan.message }}</div>
              <div class="plan-text" style="color: #ff0000;" v-if="$store.state.terminal.lastPlan.error">ERROR: {{ $store.state.terminal.lastPlan.error }}</div>
              <div class="plan-text" style="color: #ff9900;" v-if="$store.state.terminal.lastPlan.validationFailed">
                VALIDATION FAILED: {{ JSON.stringify($store.state.terminal.lastPlan.validationErrors) }}
              </div>
            </div>

            <!-- INTENT PHASE: Show AI response content -->
            <div v-if="$store.state.terminal.lastPlan.phase === 'intent'" class="intent-response">
              <!-- AI Response Display -->
              <div class="intent-fallback" style="padding: 20px; border: 1px solid #333; border-radius: 8px; background: #1a1a1a;">
                <div class="intent-header" style="margin-bottom: 15px;">
                  <h3 style="color: #00ff00; margin-bottom: 10px;">Intent Analysis</h3>
                  <p style="color: #fff;"><strong>User Request:</strong> {{ $store.state.terminal.lastPlan.intent || 'Processing...' }}</p>
                </div>
                <div class="intent-content" style="margin-bottom: 15px;">
                  <h4 style="color: #00ff00; margin-bottom: 8px;">AI Response</h4>
                  <p style="color: #ccc; line-height: 1.5;">{{ $store.state.terminal.lastPlan.response || 'No response yet' }}</p>
                </div>
                <div class="intent-capabilities" style="margin-bottom: 15px;" v-if="$store.state.terminal.lastPlan.capabilities && $store.state.terminal.lastPlan.capabilities.length > 0">
                  <h4 style="color: #00ff00; margin-bottom: 8px;">Capabilities</h4>
                  <div style="display: flex; flex-wrap: wrap; gap: 5px;">
                    <span v-for="capability in $store.state.terminal.lastPlan.capabilities" :key="capability" 
                          style="background: #333; color: #00ff00; padding: 4px 8px; border-radius: 4px; font-size: 12px;">
                      {{ capability }}
                    </span>
                  </div>
                </div>
                
                <!-- Validation Results and Preview -->
                <div class="validation-section" style="margin-bottom: 15px;" v-if="validatedRecommendations">
                  <h4 style="color: #00ff00; margin-bottom: 8px;">XMBL Structure Validation</h4>
                  
                  <!-- Validation Status -->
                  <div style="margin-bottom: 10px;">
                    <span :style="validatedRecommendations.isValid ? 'color: #00ff00;' : 'color: #ff6600;'">
                      {{ validatedRecommendations.isValid ? '✅ All recommendations valid' : '⚠️ Some recommendations invalid' }}
                    </span>
                  </div>
                  
                  <!-- Valid Components -->
                  <div v-if="validatedRecommendations.components.valid.length > 0" style="margin-bottom: 10px;">
                    <h5 style="color: #00ff00; margin-bottom: 5px; font-size: 14px;">Valid Components:</h5>
                    <div style="display: flex; flex-wrap: wrap; gap: 5px;">
                      <span v-for="comp in validatedRecommendations.components.valid" :key="comp.name"
                            style="background: #004400; color: #00ff00; padding: 4px 8px; border-radius: 4px; font-size: 12px; cursor: pointer;"
                            :title="`Props: ${JSON.stringify(comp.props)}`">
                        {{ comp.name }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- Valid Functions -->
                  <div v-if="validatedRecommendations.functions.valid.length > 0" style="margin-bottom: 10px;">
                    <h5 style="color: #00ff00; margin-bottom: 5px; font-size: 14px;">Valid Functions:</h5>
                    <div style="display: flex; flex-wrap: wrap; gap: 5px;">
                      <span v-for="func in validatedRecommendations.functions.valid" :key="func.name"
                            style="background: #004400; color: #00ff00; padding: 4px 8px; border-radius: 4px; font-size: 12px; cursor: pointer;"
                            :title="`Signature: ${func.signature}`">
                        {{ func.name }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- Valid Actions -->
                  <div v-if="validatedRecommendations.actions.valid.length > 0" style="margin-bottom: 10px;">
                    <h5 style="color: #00ff00; margin-bottom: 5px; font-size: 14px;">Valid Actions:</h5>
                    <div style="display: flex; flex-wrap: wrap; gap: 5px;">
                      <span v-for="action in validatedRecommendations.actions.valid" :key="action.name"
                            style="background: #004400; color: #00ff00; padding: 4px 8px; border-radius: 4px; font-size: 12px; cursor: pointer;"
                            :title="`Signature: ${action.signature}`">
                        {{ action.name }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- Invalid Items -->
                  <div v-if="!validatedRecommendations.isValid" style="margin-bottom: 10px;">
                    <h5 style="color: #ff6600; margin-bottom: 5px; font-size: 14px;">Invalid Items:</h5>
                    <div style="display: flex; flex-wrap: wrap; gap: 5px;">
                      <span v-for="error in validatedRecommendations.errors" :key="error"
                            style="background: #440000; color: #ff6600; padding: 4px 8px; border-radius: 4px; font-size: 12px;">
                        {{ error }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- Enhanced Preview Section -->
                  <div v-if="validatedRecommendations.isValid && previewComponents.length > 0" style="margin-top: 10px; padding: 10px; background: #1a1a1a; border: 1px solid #333; border-radius: 4px;">
                    <h5 style="color: #00ff00; margin-bottom: 8px; font-size: 14px;">XMBL Preview:</h5>
                    <div class="preview-list" style="display: flex; flex-direction: column; gap: 8px;">
                      <div v-for="preview in previewComponents" :key="`${preview.type}-${preview.name}`" 
                           class="preview-item" 
                           :class="`preview-${preview.type}`"
                           style="padding: 8px; background: #2a2a2a; border-radius: 4px; border-left: 3px solid #00ff00;">
                        <div class="preview-header" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                          <span class="preview-type" style="color: #00ff00; font-weight: bold; font-size: 12px; text-transform: uppercase;">{{ preview.type }}</span>
                          <span class="preview-name" style="color: #fff; font-weight: bold;">{{ preview.name }}</span>
                        </div>
                        <div class="preview-code" style="color: #ccc; font-size: 11px; font-family: monospace; background: #1a1a1a; padding: 4px; border-radius: 2px; overflow-x: auto;">
                          {{ preview.preview }}
                        </div>
                        <div v-if="preview.signature" class="preview-signature" style="color: #888; font-size: 10px; margin-top: 2px;">
                          {{ preview.signature }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="intent-actions" style="display: flex; gap: 10px;">
                  <button @click="acceptIntent()" class="accept-btn" style="padding: 10px 20px; background: #00aa00; color: white; border: none; border-radius: 4px; cursor: pointer;">Yes</button>
                  <button @click="rejectIntent()" class="reject-btn" style="padding: 10px 20px; background: #aa0000; color: white; border: none; border-radius: 4px; cursor: pointer;">No</button>
                </div>
              </div>
            </div>

            <!-- PRESENTING PHASE: Show options for user to choose -->
            <div v-if="$store.state.terminal.lastPlan.phase === 'presenting' && $store.state.terminal.lastPlan.options" class="options-area">
              <div class="options-header">Choose an Option</div>
              <button
                v-for="(option, index) in $store.state.terminal.lastPlan.options"
                :key="index"
                @click="$store.dispatch('agent/handleUserChoice', { 
                  choice: option, 
                  originalMessage: $store.state.terminal.currentMessage 
                })"
                class="option-button"
              >
                {{ option }}
              </button>
            </div>

            <!-- COMPOSING/COMPLETE PHASE: Show actions -->
            <div v-if="($store.state.terminal.lastPlan.phase === 'composing' || $store.state.terminal.lastPlan.phase === 'complete') && $store.state.terminal.lastPlan.actions && $store.state.terminal.lastPlan.actions.length > 0" class="actions-area">
              <div class="actions-header">Proposed Actions</div>
              <div
                v-for="(action, index) in $store.state.terminal.lastPlan.actions"
                :key="index"
                class="action-card"
              >
                <div class="action-number">{{ index + 1 }}</div>
                <div class="action-details">
                  <div class="action-name">{{ typeof action === 'string' ? action : (action.action || action.name) }}</div>
                  <div class="action-desc" v-if="typeof action === 'object'">{{ action.description || action.reason || '' }}</div>
                  <div class="action-payload" v-if="typeof action === 'object'">{{ JSON.stringify(action.payload || action.data || {}) }}</div>
                </div>
              </div>
              <div class="bulk-actions" v-if="$store.state.terminal.lastPlan.phase === 'complete'">
                <button @click="$store.dispatch('agent/executePlan', $store.state.terminal.lastPlan)" class="execute-all-btn">✓ Approve & Execute</button>
                <button @click="$store.commit('set', {keys: ['terminal', 'lastPlan'], val: null})" class="deny-btn">✗ Deny</button>
              </div>
            </div>

            <!-- COMPOSING/COMPLETE PHASE: Show components -->
            <div v-if="($store.state.terminal.lastPlan.phase === 'composing' || $store.state.terminal.lastPlan.phase === 'complete') && $store.state.terminal.lastPlan.components && $store.state.terminal.lastPlan.components.length > 0" class="components-area">
              <div class="components-header">Suggested Components</div>
              <div
                v-for="(comp, index) in $store.state.terminal.lastPlan.components"
                :key="index"
                class="component-card"
              >
                <div class="component-name">{{ typeof comp === 'string' ? comp : comp.name }}</div>
                <div class="component-desc" v-if="typeof comp === 'object'">{{ comp.description || '' }}</div>
                <div class="component-props" v-if="typeof comp === 'object'">{{ JSON.stringify(comp.props || {}) }}</div>
              </div>
            </div>

            <!-- PAGE PREVIEW (string) -->
            <div v-if="$store.state.terminal.lastPlan.page && typeof $store.state.terminal.lastPlan.page === 'string'" class="preview-area">
              <div class="preview-header">Page: {{ $store.state.terminal.lastPlan.page }}</div>
            </div>

            <!-- PAGE PREVIEW (object with data) -->
            <div v-if="$store.state.terminal.lastPlan.page && typeof $store.state.terminal.lastPlan.page === 'object' && $store.state.terminal.lastPlan.page.data" class="preview-area">
              <div class="preview-header">Page Preview: {{ $store.state.terminal.lastPlan.page.name }}</div>
              <div class="preview-page">
                <div class="page-structure">
                  <div><b>Text:</b> {{ $store.state.terminal.lastPlan.page.data.text }}</div>
                  <div><b>Styles:</b> {{ JSON.stringify($store.state.terminal.lastPlan.page.data.styles) }}</div>
                  <div><b>Components:</b></div>
                  <div v-for="(comp, idx) in $store.state.terminal.lastPlan.page.data.components" :key="idx" class="component-preview-item">
                    - {{ comp.name }} {{ JSON.stringify(comp.props || {}) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- COMPONENT PREVIEW -->
            <div v-if="$store.state.terminal.previewComponent" class="preview-area">
              <div class="preview-header">Component Preview</div>
              <div class="preview-component">
                <component :is="$store.state.terminal.previewComponent.name" v-bind="$store.state.terminal.previewComponent.props"></component>
              </div>
            </div>

            <!-- EXECUTE ACTIONS -->
            <div v-if="($store.state.terminal.lastPlan.phase === 'composing' || $store.state.terminal.lastPlan.phase === 'complete') && ($store.state.terminal.lastPlan.function || $store.state.terminal.lastPlan.page || ($store.state.terminal.lastPlan.actions && $store.state.terminal.lastPlan.actions.length > 0))" class="execute-area">
              <button class="execute-button" @click="executeComposition">
                <span v-if="$store.state.terminal.lastPlan.function">Create Function</span>
                <span v-else-if="$store.state.terminal.lastPlan.page">Create Page</span>
                <span v-else>Execute Actions</span>
              </button>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="no-selection">No AI response yet. Send a prompt to get started.</div>
        </template>
      </div>

      <!-- Right Column: Context Panel -->
      <div class="right-panel xs-font" style="display: flex; flex-direction: column; overflow: hidden;">
        <div class="panel-header">
          Context
          <button @click="$store.commit('set', {keys: ['show', 'addContext'], val: !$store.state.show.addContext})" class="add-context-btn">+</button>
        </div>
        
        <!-- Add Context Form -->
        <div v-if="$store.state.show.addContext" class="add-context-form">
          <select v-model="$store.state.terminal.contextType" class="context-type-select">
            <option value="state">State Path</option>
            <option value="function">Function</option>
            <option value="text">Text</option>
            <option value="json">JSON</option>
          </select>
          
          <input
            v-if="$store.state.terminal.contextType === 'state'"
            v-model="$store.state.terminal.contextInput"
            placeholder="e.g., state.app.basics.items"
            class="context-input"
          />
          
          <textarea
            v-if="$store.state.terminal.contextType === 'text' || $store.state.terminal.contextType === 'json'"
            v-model="$store.state.terminal.contextInput"
            :placeholder="$store.state.terminal.contextType === 'json' ? 'Enter JSON...' : 'Enter text...'"
            class="context-textarea"
          ></textarea>
          
          <select
            v-if="$store.state.terminal.contextType === 'function'"
            v-model="$store.state.terminal.contextInput"
            class="context-function-select"
          >
            <option v-for="fn in dispatchActions" :key="fn" :value="fn">{{ fn }}</option>
          </select>
          
          <button @click="addContextItem()" class="add-btn">Add to Context</button>
        </div>
        
        <div class="context-list" style="flex: 1; overflow-y: auto; min-height: 0;" :key="`context-${$store.state.terminal.currentConversationId}-${$store.state.terminal.contextKey || 0}`">
          <!-- State component for editing terminal state -->
          <div class="state-editor" style="margin-bottom: 10px; padding: 10px; border: 1px solid #333; border-radius: 4px;">
            <h4 style="color: #00ff00; margin-bottom: 8px;">Terminal State Editor</h4>
            <state 
              :thisstatedata="$store.state.terminal" 
              :thisparent="'terminal'"
              editable="true"
              createable="true"
              :nesting="0"
            />
          </div>
          
          <div
            v-for="(item, index) in $store.state.terminal.context || []"
            :key="`${index}-${$store.state.terminal.currentConversationId}-${$store.state.terminal.contextKey || 0}`"
            class="context-item"
            style="margin-bottom: 10px; padding: 10px; border: 1px solid #333; border-radius: 4px;"
          >
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
              <h4 style="color: #00ff00; margin: 0;">{{ item.type || 'text' }}</h4>
              <button @click="removeContextItem(index)" style="background: #aa0000; color: white; border: none; border-radius: 4px; padding: 4px 8px; cursor: pointer;">×</button>
            </div>
            <state 
              :thisstatedata="item" 
              :thisparent="`terminal.context[${index}]`"
              editable="true"
              createable="true"
              :nesting="1"
            />
          </div>
          <div v-if="!$store.state.terminal.context || !$store.state.terminal.context.length" class="context-empty">
            No context items. Click + to add.
          </div>
        </div>
      </div>
    </div>

    <!-- User Prompt Area -->
    <div class="prompt-section">
      <div class="prompt-input-container">
        <textarea
          v-model="$store.state.terminal.currentMessage"
          @keydown.enter.ctrl="sendMessage()"
          class="prompt-input"
          placeholder="Enter your prompt... (Ctrl+Enter to send)"
        ></textarea>
        <button @click="sendMessage()" class="send-btn">▶</button>
      </div>
    </div>

    <!-- Footer Controls -->
    <div class="footer-controls">
      <div class="model-selector">
        <label>Model:</label>
        <select v-model="$store.state.terminal.selectedModel" class="model-dropdown">
          <option v-for="model in $store.state.terminal.availableModels || []" :key="model" :value="model">{{ model }}</option>
        </select>
        <button @click="$store.dispatch('agent/getOllamaModels')" class="refresh-btn">↻</button>
      </div>
      <div class="token-info" :key="`tokens-${$store.state.terminal.currentConversationId}`">
        <span class="token-label">Tokens:</span>
        <span class="token-count">{{ tokenCount }}</span>
        <span class="token-separator">/</span>
        <span class="token-max">{{ modelMaxTokens }}</span>
        <span v-if="$store.state.terminal.lastTokenCount" class="last-token-count" style="margin-left: 10px; color: #00ff00;">
          (Last: {{ $store.state.terminal.lastTokenCount }})
        </span>
      </div>
      <div v-if="isLocalhost || $store.state.debug" class="debug-controls xs-font">
        <button @click="$store.dispatch('agent/introspect')" class="debug-btn">Introspect</button>
        <button @click="$store.dispatch('agent/initAgent')" class="debug-btn">Init</button>
        <button 
          v-if="$store.state.terminal.debugLogs && $store.state.terminal.debugLogs.length > 0"
          @click="$store.commit('set', {keys: ['terminal', 'showDebugConsole'], val: !$store.state.terminal.showDebugConsole})" 
          class="debug-btn"
          :key="`debug-${$store.state.terminal.debugLogs.length}`"
        >
          {{ $store.state.terminal.showDebugConsole ? '⊗' : '☰' }} Debug ({{ $store.state.terminal.debugLogs.length }})
        </button>
      </div>
    </div>

    <!-- Debug Console -->
    <div v-if="$store.state.terminal.showDebugConsole" class="debug-console" :key="`debug-console-${$store.state.terminal.currentConversationId}`">
      <div class="debug-console-header">
        <span class="debug-console-title">🔍 AI System Debug Log</span>
        <button @click="$store.commit('set', {keys: ['terminal', 'debugLogs'], val: []})" class="debug-clear-btn">Clear</button>
      </div>
      <div class="debug-console-body" :key="`debug-logs-${$store.state.terminal.debugLogs.length}`">
          <div
            v-for="(log, index) in $store.state.terminal.debugLogs || []"
            :key="`log-${log.timestamp}-${index}`"
            :class="['debug-log-entry', `log-type-${log.type}`, {expanded: $store.state.terminal.expandedDebugLogs && $store.state.terminal.expandedDebugLogs[index]}]"
            @click="toggleDebugLog(index)"
          >
          <div class="debug-log-header">
            <span class="debug-log-time">{{ new Date(log.timestamp).toLocaleTimeString() }}</span>
            <span class="debug-log-type">{{ log.type }}</span>
            <span class="debug-log-summary">{{ log.summary }}</span>
          </div>
          <div v-if="$store.state.terminal.expandedDebugLogs && $store.state.terminal.expandedDebugLogs[index]" class="debug-log-details">
            <div v-if="log.data.call" class="debug-call">
              <div class="debug-call-label">CALLED:</div>
              <pre>{{ JSON.stringify(log.data.call, null, 2) }}</pre>
            </div>
            <div v-if="log.data.result" class="debug-result">
              <div class="debug-result-label">RETURNED:</div>
              <pre>{{ JSON.stringify(log.data.result, null, 2) }}</pre>
            </div>
            <div v-if="!log.data.call && !log.data.result" class="debug-other">
              <pre>{{ JSON.stringify(log.data, null, 2) }}</pre>
            </div>
          </div>
        </div>
        <div v-if="!$store.state.terminal.debugLogs || $store.state.terminal.debugLogs.length === 0" class="debug-log-empty">
          No debug logs yet. Interact with the AI to see activity.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Terminal',
  computed: {
    isLocalhost() {
      return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    },
    dispatchActions() {
      // Use introspect data if available, fallback to store
      if (this.$store.state.agent.introspection && this.$store.state.agent.introspection.dispatchActions) {
        return this.$store.state.agent.introspection.dispatchActions.map(action => action.name);
      }
      return Object.keys(this.$store.state.functions?.dispatch || {});
    },
    tokenCount() {
      const message = this.$store.state.terminal.currentMessage || ''
      return this.estimateTokenCount(message)
    },
    
    modelMaxTokens() {
      const model = this.$store.state.terminal.selectedModel
      if (!model) return '?'
      
      // Map known models to their context windows
      const modelLimits = {
        'llama3.2:latest': '128K',
        'llama3.2:1b': '128K',
        'llama3.2:3b': '128K',
        'qwen3-coder:480b-cloud': '128K',
        'deepseek-v3.1:671b-cloud': '64K',
        'gpt-oss:120b-cloud': '32K',
        'gpt-oss:latest': '32K',
        'phi4:latest': '16K',
        'smollm2:1.7b': '8K'
      }
      
      return modelLimits[model] || '?'
    },
    terminalTabsCount() {
      const aiCount = Object.keys(this.$store.state.msgs || {}).filter(id => this.$store.state.msgs[id] && this.$store.state.msgs[id].type === 'ai').length
      return aiCount
    },
    currentConversation() {
      const id = this.$store.state.terminal.currentConversationId
      return id ? this.$store.state.msgs[id] : null
    },
    userPrompts() {
      if (!this.currentConversation || !this.currentConversation.banter) return []
      return Object.values(this.currentConversation.banter)
        .filter(msg => {
          // Only include messages that are NOT from the AI user
          // AI user ID is '5nA8rryyPedDsIvtWmTD0pRbann2'
          // Check both 'user' and 'userId' fields
          const userId = msg.user || msg.userId
          return userId && userId !== '5nA8rryyPedDsIvtWmTD0pRbann2'
        })
        .sort((a, b) => parseInt(a.timestamp || 0) - parseInt(b.timestamp || 0))
    },
    latestUserPrompt() {
      return this.userPrompts.length > 0 ? this.userPrompts[this.userPrompts.length - 1] : null
    },
    
    // Dynamic component structure from AI
    dynamicComponentStructure() {
      const plan = this.$store.state.terminal.lastPlan
      if (!plan || plan.phase !== 'intent' || !plan.component_structure) return null
      
      return plan.component_structure
    },
    
    // Get introspect data for validation
    introspectData() {
      return this.$store.state.agent?.introspection || {}
    },
    
    // Validate AI recommendations against introspect structure
    validatedRecommendations() {
      const plan = this.$store.state.terminal.lastPlan
      if (!plan) return null
      
      const introspect = this.introspectData
      const validation = {
        components: this.validateComponents(plan.recommended_components || [], introspect),
        functions: this.validateFunctions(plan.recommended_functions || [], introspect),
        actions: this.validateFunctionActions(plan.recommended_actions || [], introspect),
        isValid: true,
        errors: []
      }
      
      // Check if any validation failed
      if (validation.components.invalid.length > 0 || 
          validation.functions.invalid.length > 0 || 
          validation.actions.invalid.length > 0) {
        validation.isValid = false
        validation.errors = [
          ...validation.components.invalid.map(c => `Invalid component: ${c}`),
          ...validation.functions.invalid.map(f => `Invalid function: ${f}`),
          ...validation.actions.invalid.map(a => `Invalid action: ${a}`)
        ]
      }
      
      return validation
    },
    
    // Generate preview components from validated recommendations
    previewComponents() {
      const validation = this.validatedRecommendations
      if (!validation || !validation.isValid) return []
      
      const previews = []
      
      // Generate component previews
      validation.components.valid.forEach(comp => {
        previews.push({
          type: 'component',
          name: comp.name,
          props: comp.props,
          preview: this.generateComponentPreview(comp.name, comp.props)
        })
      })
      
      // Generate function previews
      validation.functions.valid.forEach(func => {
        previews.push({
          type: 'function',
          name: func.name,
          signature: func.signature,
          preview: this.generateFunctionPreview(func.name, func.signature)
        })
      })
      
      // Generate action previews
      validation.actions.valid.forEach(action => {
        previews.push({
          type: action.type,
          name: action.name,
          signature: action.signature,
          preview: this.generateActionPreview(action.name, action.type)
        })
      })
      
      return previews
    },
    
    // Generate component preview
    generateComponentPreview(componentName, props) {
      return `<${componentName} ${Object.entries(props || {}).map(([key, value]) => `${key}="${value}"`).join(' ')} />`
    },
    
    // Generate function preview
    generateFunctionPreview(functionName, signature) {
      return `function ${functionName}(${signature}) { /* XMBL function */ }`
    },
    
    // Generate action preview
    generateActionPreview(actionName, type) {
      if (type === 'function') {
        return `<component :is="${actionName}" />`
      } else {
        return `dispatch('${actionName}')`
      }
    },
    
    // Get available XMBL components for AI composer
    availableComponents() {
      const introspect = this.introspectData
      return Object.keys(introspect.components || {})
    },
    
    // Get available XMBL functions for AI composer
    availableFunctions() {
      const introspect = this.introspectData
      return Object.keys(introspect.functions || {})
    },
    
    // Get available XMBL actions for AI composer
    availableActions() {
      const introspect = this.introspectData
      return introspect.dispatchActions?.map(a => a.name) || []
    },
    
    // Get available XMBL models for AI composer
    availableModels() {
      const introspect = this.introspectData
      return Object.keys(introspect.models || {})
    },
    
    // Get available XMBL pages for AI composer
    availablePages() {
      const introspect = this.introspectData
      return Object.keys(introspect.pages || {})
    },
    
    // Get available XMBL items for AI composer
    availableItems() {
      const introspect = this.introspectData
      return Object.keys(introspect.items || {})
    },
    
  },


  methods: {
    // Validation methods for introspect structure
    validateComponents(recommendedComponents, introspect) {
      const availableComponents = introspect.components || {}
      const valid = []
      const invalid = []
      
      recommendedComponents.forEach(component => {
        if (availableComponents[component]) {
          valid.push({
            name: component,
            data: availableComponents[component],
            props: this.getComponentProps(component, introspect)
          })
        } else {
          invalid.push(component)
        }
      })
      
      return { valid, invalid }
    },
    
    validateFunctions(recommendedFunctions, introspect) {
      const availableFunctions = introspect.functions || {}
      const valid = []
      const invalid = []
      
      recommendedFunctions.forEach(func => {
        if (availableFunctions[func]) {
          valid.push({
            name: func,
            data: availableFunctions[func],
            signature: this.getFunctionSignature(func, introspect)
          })
        } else {
          invalid.push(func)
        }
      })
      
      return { valid, invalid }
    },
    
    validateActions(recommendedActions, introspect) {
      const availableActions = introspect.dispatchActions || []
      const valid = []
      const invalid = []
      
      recommendedActions.forEach(action => {
        const found = availableActions.find(a => a.name === action || a.action === action)
        if (found) {
          valid.push({
            name: action,
            data: found,
            signature: found.signature
          })
        } else {
          invalid.push(action)
        }
      })
      
      return { valid, invalid }
    },
    
    getComponentProps(componentName, introspect) {
      // Get component props from models or component data
      const models = introspect.models || {}
      const components = introspect.components || {}
      
      // Check if component exists in models
      if (models[componentName]) {
        return models[componentName].props || {}
      }
      
      // Check if component exists in components
      if (components[componentName]) {
        return components[componentName].props || {}
      }
      
      return {}
    },
    
    getFunctionSignature(functionName, introspect) {
      const functions = introspect.functions || {}
      if (functions[functionName]) {
        return functions[functionName].signature || 'Unknown signature'
      }
      return 'Unknown signature'
    },
    
    // Get response data for a specific prompt
    getResponseForPrompt(promptMsg) {
      const conversationId = this.$store.state.terminal.currentConversationId
      const banter = this.$store.state.msgs[conversationId]?.banter
      if (!banter) return null
      
      const banterArray = Object.values(banter).sort((a, b) => parseInt(a.id || a.timestamp || 0) - parseInt(b.id || b.timestamp || 0))
      const userMsgIndex = banterArray.findIndex(m => m.id === promptMsg.id || m.text === promptMsg.text)
      
      if (userMsgIndex !== -1) {
        // Find the next AI response after this user message
        for (let i = userMsgIndex + 1; i < banterArray.length; i++) {
          const response = banterArray[i]
          if ((response.userId === '5nA8rryyPedDsIvtWmTD0pRbann2' || response.user === '5nA8rryyPedDsIvtWmTD0pRbann2') && response.text) {
            try {
              // Try to parse the AI response
              const parsed = JSON.parse(response.text)
              return parsed
            } catch (e) {
              // If not JSON, return basic response
              return {
                intent: response.text,
                response: response.text,
                recommended_components: [],
                recommended_functions: [],
                recommended_actions: []
              }
            }
          }
        }
      }
      return null
    },
    
    // Enhanced validation that includes function actions
    validateFunctionActions(recommendedActions, introspect) {
      const availableActions = introspect.dispatchActions || []
      const valid = []
      const invalid = []
      
      recommendedActions.forEach(action => {
        // Check if it's a direct action
        const found = availableActions.find(a => a.name === action || a.action === action)
        if (found) {
          valid.push({
            name: action,
            data: found,
            signature: found.signature,
            type: 'action'
          })
        } else {
          // Check if it's a function that can be called with :is="action"
          const functions = introspect.functions || {}
          if (functions[action]) {
            valid.push({
              name: action,
              data: functions[action],
              signature: functions[action].signature || 'Unknown signature',
              type: 'function'
            })
          } else {
            invalid.push(action)
          }
        }
      })
      
      return { valid, invalid }
    },
    
    // Dynamic component renderer
    renderDynamicComponent(componentData) {
      if (!componentData || !componentData.component) {
        return null
      }
      
      const componentName = componentData.component
      const props = componentData.props || {}
      const children = componentData.children || componentData.components || []
      
      // Handle nested components
      if (children && children.length > 0) {
        props.children = children.map(child => this.renderDynamicComponent(child)).filter(child => child !== null)
      }
      
      // Handle layout component with header/content/footer structure
      if (componentName === 'layout' && props.header && props.content && props.footer) {
        // For layout components, we need to render the content section
        if (props.content && props.content.components && props.content.components.length > 0) {
          // Render the first content component
          const contentComponent = props.content.components[0]
          return this.renderDynamicComponent(contentComponent)
        }
      }
      
      return {
        component: componentName,
        props: props
      }
    },
    
    estimateTokenCount(text) {
      if (!text) return 0
      // Rough estimate: 1 token ≈ 4 characters for English text
      return Math.ceil(text.length / 4)
    },
    
    getCapabilityDescription(capability) {
      const descriptions = {
        'create': 'Create a new page, component, item or function in an xmbl instance.',
        'manage': 'Manage app state, styles, users, pages, components, or functions from the introspection data in an xmbl instance.',
        'delete': 'Delete a page, component, item or function in an xmbl instance.',
        'update': 'Update a page, component, item, users, or functions in an xmbl instance.'
      }
      return descriptions[capability] || 'XMBL capability'
    },
    loadPlanFromHistory(msg) {
      this.$store.commit('set', {keys: ['terminal', 'selectedPrompt'], val: msg})
      
      const conversationId = this.$store.state.terminal.currentConversationId
      const banter = this.$store.state.msgs[conversationId].banter
      const banterArray = Object.values(banter).sort((a, b) => parseInt(a.id || a.timestamp || 0) - parseInt(b.id || b.timestamp || 0))
      
      // Look for an AI response that matches this user message
      let aiResponse = null
      const userMsgIndex = banterArray.findIndex(m => m.id === msg.id || m.text === msg.text)
      
      if (userMsgIndex !== -1) {
        // Find the next AI response after this user message
        for (let i = userMsgIndex + 1; i < banterArray.length; i++) {
          const response = banterArray[i]
          if ((response.userId === '5nA8rryyPedDsIvtWmTD0pRbann2' || response.user === '5nA8rryyPedDsIvtWmTD0pRbann2') && response.text) {
            aiResponse = response
            break
          }
        }
      }
      
      if (aiResponse) {
        this.$store.dispatch('agent/loadPlanFromHistory', aiResponse)
        // Load context from the AI response
        if (aiResponse.context && Array.isArray(aiResponse.context)) {
          this.$store.commit('set', {keys: ['terminal', 'context'], val: aiResponse.context})
        }
      } else {
        // No AI response found, clear the response area
        this.$store.commit('set', {keys: ['terminal', 'lastPlan'], val: null})
        this.$store.commit('set', {keys: ['terminal', 'context'], val: []})
      }
    },
    
    selectLatestPrompt() {
      if (this.latestUserPrompt) {
        // Check if this prompt has an AI response
        const conversationId = this.$store.state.terminal.currentConversationId
        const conversation = this.$store.state.msgs[conversationId]
        const banter = conversation?.banter || {}
        const banterArray = Object.values(banter).sort((a, b) => parseInt(a.id || a.timestamp || 0) - parseInt(b.id || b.timestamp || 0))
        const userMsgIndex = banterArray.findIndex(m => m.id === this.latestUserPrompt.id)
        const aiResponse = banterArray.find((m, idx) => idx > userMsgIndex && (m.userId === '5nA8rryyPedDsIvtWmTD0pRbann2' || m.user === '5nA8rryyPedDsIvtWmTD0pRbann2'))
        
        if (aiResponse) {
          // Load existing AI response
          this.loadPlanFromHistory(aiResponse)
        } else {
          // No AI response exists, process the prompt
          this.$store.dispatch('agent/sendUserMessage', { 
            message: this.latestUserPrompt.text,
            phase: 'intent'
          })
        }
      }
    },
    
    async sendMessage() {
      const message = this.$store.state.terminal.currentMessage
      if (!message.trim()) return
      
      // Get current context to append to the message
      // const context = this.$store.state.terminal.context || []
      
      // Send the message for intent phase
      await this.$store.dispatch('agent/sendUserMessage', { 
        message: message,
        phase: 'intent'
      })
      
      // Clear the input
      this.$store.commit('set', {keys: ['terminal', 'currentMessage'], val: ''})
      
      // Update keys to trigger reactivity
      this.$store.commit('set', {keys: ['terminal', 'historyKey'], val: (this.$store.state.terminal.historyKey || 0) + 1})
      this.$store.commit('set', {keys: ['terminal', 'tabKey'], val: (this.$store.state.terminal.tabKey || 0) + 1})
      this.$store.commit('set', {keys: ['terminal', 'planKey'], val: (this.$store.state.terminal.planKey || 0) + 1})
      this.$store.commit('set', {keys: ['terminal', 'contextKey'], val: (this.$store.state.terminal.contextKey || 0) + 1})
      
      // Update terminal count in state
      const terminalCount = Object.keys(this.$store.state.msgs || {}).filter(id => this.$store.state.msgs[id] && this.$store.state.msgs[id].type === 'ai').length
      this.$store.commit('set', {keys: ['counts', 'terminal'], val: terminalCount})
      
      // The AI response is already set in lastPlan by sendUserMessage action
      // No need to call loadPlanFromHistory here
    },
    
    acceptIntent() {
      // Move to next phase (introspect)
      this.$store.dispatch('agent/acceptIntent')
    },
    
    rejectIntent() {
      // Stay in intent phase, add to context
      this.$store.dispatch('agent/rejectIntent')
    },
    
    addContextItem() {
      const type = this.$store.state.terminal.contextType
      const input = this.$store.state.terminal.contextInput
      
      if (!input) return
      
      let value = input
      let label = input
      
      if (type === 'state') {
        const path = input.replace('state.', '').split('.')
        value = path.reduce((obj, key) => obj?.[key], this.$store.state)
        label = `State: ${input}`
      } else if (type === 'json') {
        try {
          value = JSON.parse(input)
          label = 'JSON Data'
        } catch (e) {
          alert('Invalid JSON')
          return
        }
      } else if (type === 'function') {
        label = `Function: ${input}`
      }
      
      const items = this.$store.state.terminal.context.items || []
      items.push({ type, label, value, id: Date.now() })
      
      this.$store.commit('set', {
        keys: ['terminal', 'context', 'items'],
        val: items
      })
      
      this.$store.commit('set', {keys: ['terminal', 'contextInput'], val: ''})
      this.$store.commit('set', {keys: ['show', 'addContext'], val: false})
    },
    
    removeContextItem(index) {
      const items = this.$store.state.terminal.context.items || []
      items.splice(index, 1)
      this.$store.commit('set', {
        keys: ['terminal', 'context', 'items'],
        val: items
      })
    },
    
    toggleDebugLog(index) {
      const expandedLogs = this.$store.state.terminal.expandedDebugLogs || []
      const current = expandedLogs[index]
      this.$store.commit('set', {
        keys: ['terminal', 'expandedDebugLogs', index],
        val: !current
      })
      this.$forceUpdate()
    },
    
    loadLastPlanFromConversation() {
      const conversationId = this.$store.state.terminal.currentConversationId
      if (!conversationId) return
      
      const conversation = this.$store.state.msgs[conversationId]
      if (!conversation || !conversation.banter) return
      
      const banterEntries = Object.entries(conversation.banter).sort(([aKey], [bKey]) => Number(bKey) - Number(aKey))
      
      for (const [, entry] of banterEntries) {
        if (entry.user === this.$store.state.agent.agentUserId || entry.userId === this.$store.state.agent.agentUserId) {
          try {
            const parsed = JSON.parse(entry.text)
            if (parsed.agentType === 'converse') {
              this.$store.commit('set', {keys: ['terminal', 'lastPlan'], val: parsed})
              console.log('[TERMINAL] Loaded last plan from conversation:', parsed)
              break
            }
          } catch (e) {
            // Not JSON, skip
          }
        }
      }
    },
    
    async executeComposition() {
      const plan = this.$store.state.terminal.lastPlan
      if (!plan) return
      
      console.log('[TERMINAL] Executing composition...', plan)
      
      // If there's a function to create
      if (plan.function) {
        const functionName = plan.function.name || 'newFunction'
        const functionData = plan.function.data || plan.function
        
        const result = await this.$store.dispatch('agent/createFunction', {
          functionName,
          functionData
        })
        
        if (result.success) {
          alert(`Function "${functionName}" created successfully! View in Admin > Advanced > Functions.`)
          console.log('[TERMINAL] Function created:', result)
        } else {
          alert(`Failed to create function: ${result.errors.join(', ')}`)
          console.error('[TERMINAL] Function creation failed:', result.errors)
        }
      }
      
      // If there's a page to create
      if (plan.page) {
        const pageName = plan.page.name || plan.page.title || plan.page.id || 'new-page'
        const pageData = plan.page.data || plan.page
        
        const result = await this.$store.dispatch('agent/createPage', {
          pageName,
          pageData
        })
        
        if (result.success) {
          alert(`Page "${pageName}" created successfully! Navigate to /page/${pageName} to view it.`)
          console.log('[TERMINAL] Page created:', result)
        } else {
          alert(`Failed to create page: ${result.errors.join(', ')}`)
          console.error('[TERMINAL] Page creation failed:', result.errors)
        }
      }
      
      // If there are actions to execute
      if (plan.actions && plan.actions.length > 0) {
        for (const action of plan.actions) {
          try {
            const result = await this.$store.dispatch(action.action || action.name, action.payload || action.data)
            console.log(`[TERMINAL] Action "${action.action || action.name}" executed:`, result)
          } catch (error) {
            console.error(`[TERMINAL] Action "${action.action || action.name}" failed:`, error)
            alert(`Action "${action.action || action.name}" failed: ${error.message}`)
          }
        }
      }
    }
  },

  async mounted() {
    await this.$store.dispatch('agent/initAgent')
    await this.$store.dispatch('agent/getOllamaModels')
    
    // Load messages and auto-select most recent conversation
    await this.$store.dispatch('query', { type: 'msgs' })
    
    // Auto-select most recent AI conversation
    const msgs = this.$store.state.msgs
    if (msgs && Object.keys(msgs).length > 0 && !this.$store.state.terminal.currentConversationId) {
      const aiConversations = Object.keys(msgs).filter(id => msgs[id] && msgs[id].type === 'ai').sort((a, b) => {
        const aTime = msgs[a]?.created || 0
        const bTime = msgs[b]?.created || 0
        return bTime - aTime
      })
      if (aiConversations.length > 0) {
        this.$store.commit('set', {keys: ['terminal', 'currentConversationId'], val: aiConversations[0]})
        this.$store.commit('set', {keys: ['terminal', 'tabKey'], val: 1})
        this.$store.commit('set', {keys: ['terminal', 'historyKey'], val: 1})
        this.$store.commit('set', {keys: ['terminal', 'planKey'], val: 1})
        this.$store.commit('set', {keys: ['terminal', 'contextKey'], val: 1})
        
        // Update terminal count in state
        const terminalCount = Object.keys(msgs).filter(id => msgs[id] && msgs[id].type === 'ai').length
        this.$store.commit('set', {keys: ['counts', 'terminal'], val: terminalCount})
        
        // Auto-select the latest user prompt and load its response
        this.$nextTick(() => {
          this.selectLatestPrompt()
        })
      }
    }
  }
}
</script>

<style scoped>
.ai-agent-container {
  background: #0a0a0a;
  color: #00ff00;
  font-family: 'Courier New', monospace;
  border: 1px solid #333;
  border-radius: 4px;
}

.tasks-bar {
  background: #111;
  padding: 8px;
  border-bottom: 1px solid #333;
}

.task-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
}

.task-tab {
  padding: 8px 16px;
  background: #222;
  border: 1px solid #333;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}

.task-tab:hover {
  background: #2a2a2a;
  border-color: #00ff00;
}

.task-tab.active {
  background: #00ff00;
  color: #000;
  border-color: #00ff00;
}

.add-task-btn {

  background: #00ff00;
  color: #000;
  border: none;
  cursor: pointer;
  font-weight: bold;
}

.main-layout {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.left-panel,
.right-panel {
  width: 250px;
  background: #0f0f0f;
  border-right: 1px solid #333;
  display: flex;
  flex-direction: column;
}

.right-panel {
  border-right: none;
  border-left: 1px solid #333;
}

.panel-header {

  background: #1a1a1a;
  border-bottom: 1px solid #333;
  font-weight: bold;
  text-transform: uppercase;

}

.history-list,
.context-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.history-item {
  padding: 8px;
  margin-bottom: 4px;
  background: #1a1a1a;
  border: 1px solid #222;
  cursor: pointer;
  transition: all 0.2s;
}

.history-item:hover {
  background: #222;
  border-color: #00ff00;
}

.history-item.active {
  background: #00ff00;
  color: #000;
  border-color: #00ff00;
}

.history-prompt {

  margin-bottom: 4px;
}

.history-time {
  font-size: 8px;
  color: #666;
}

.history-empty {
  padding: 16px;
  text-align: center;
  color: #666;
  font-style: italic;
}

.center-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #0a0a0a;
}

.plan-area,
.preview-area,
.no-selection {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.plan-summary {
  padding: 16px;
  background: #1a1a1a;
  border: 1px solid #333;
  margin-bottom: 16px;
}

.plan-text {
  color: #00ff00;
  line-height: 1.6;
}

.execute-area {
  margin: 16px 0;
  display: flex;
  justify-content: center;
}

.execute-button {
  padding: 12px 24px;
  background: #00ff00;
  color: #000;
  border: none;
  font-weight: bold;
  font-size: 14px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 4px;
}

.execute-button:hover {
  background: #00cc00;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 255, 0, 0.3);
}

.execute-button:active {
  transform: translateY(0);
}

.actions-area {
  margin-bottom: 16px;
}

.actions-header,
.components-header,
.preview-header {
  padding: 8px 12px;
  background: #00ff00;
  color: #000;
  font-weight: bold;
  text-transform: uppercase;

  margin-bottom: 8px;
}

.action-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #1a1a1a;
  border: 1px solid #333;
  margin-bottom: 8px;
  transition: all 0.2s;
}

.action-card:hover {
  background: #222;
  border-color: #00ff00;
}

.action-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #00ff00;
  color: #000;
  font-weight: bold;
  border-radius: 50%;
}

.action-details {
  flex: 1;
}

.action-name {
  color: #00ff00;
  font-weight: bold;
  margin-bottom: 4px;
}

.action-desc {
  color: #999;

  margin-bottom: 4px;
}

.action-payload {
  color: #666;

  font-family: 'Courier New', monospace;
  word-break: break-all;
}

.bulk-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.execute-all-btn {
  flex: 1;
  padding: 12px;
  background: #00ff00;
  color: #000;
  border: none;
  font-weight: bold;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s;
}

.execute-all-btn:hover {
  background: #00dd00;
}

.deny-btn {
  padding: 12px 24px;
  background: #ff0000;
  color: #fff;
  border: none;
  font-weight: bold;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s;
}

.deny-btn:hover {
  background: #dd0000;
}

.components-area {
  margin-top: 16px;
}

.component-card {
  padding: 12px;
  background: #1a1a1a;
  border: 1px solid #333;
  margin-bottom: 8px;
}

.component-name {
  color: #00ff00;
  font-weight: bold;
  margin-bottom: 4px;
}

.component-desc {
  color: #999;

  margin-bottom: 4px;
}

.component-props {
  color: #666;

  font-family: 'Courier New', monospace;
}

.no-selection {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  font-style: italic;
}

.prompt-section {
  padding: 16px;
  background: #111;
  border-top: 1px solid #333;
}

.prompt-input-container {
  display: flex;
  gap: 8px;
}

.prompt-input {
  flex: 1;
  background: #0a0a0a;
  border: 1px solid #333;
  color: #00ff00;
  padding: 12px;
  font-family: 'Courier New', monospace;

  resize: none;
  min-height: 40px;
  max-height: 200px;
}

.prompt-input:focus {
  outline: none;
  border-color: #00ff00;
}

.send-btn {

  background: #00ff00;
  color: #000;
  border: none;
  cursor: pointer;
  font-weight: bold;

  transition: all 0.2s;
}

.send-btn:hover {
  background: #00dd00;
}

.context-item {
  padding: 8px;
  margin-bottom: 4px;
  background: #1a1a1a;
  border: 1px solid #333;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
}

.context-item button {
  background: #ff0000;
  color: #fff;
  border: none;
  padding: 2px 6px;
  cursor: pointer;

}

.add-context-btn {
  background: #00ff00;
  color: #000;
  border: none;
  padding: 2px 8px;
  cursor: pointer;
  font-weight: bold;
  margin-left: auto;
}

.add-context-form {
  padding: 8px;
  background: #1a1a1a;
  border-bottom: 1px solid #333;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.context-type-select,
.context-function-select {
  background: #0a0a0a;
  color: #00ff00;
  border: 1px solid #333;
  padding: 6px;
  font-family: 'Courier New', monospace;
}

.context-input {
  background: #0a0a0a;
  color: #00ff00;
  border: 1px solid #333;
  padding: 6px;
  font-family: 'Courier New', monospace;
}

.context-textarea {
  background: #0a0a0a;
  color: #00ff00;
  border: 1px solid #333;
  padding: 6px;
  font-family: 'Courier New', monospace;
  min-height: 60px;
  resize: vertical;
}

.add-btn {
  background: #00ff00;
  color: #000;
  border: none;
  padding: 6px 12px;
  cursor: pointer;
  font-weight: bold;
  text-transform: uppercase;
}

.context-empty {
  padding: 16px;
  text-align: center;
  color: #666;
  font-style: italic;

}

.panel-header {
  display: flex;
  align-items: center;
}

.preview-component {
  padding: 16px;
}

.footer-controls {
  padding: 12px;
  background: #111;
  border-top: 1px solid #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.model-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.model-selector label {

  text-transform: uppercase;
}

.model-dropdown {
  background: #0a0a0a;
  color: #00ff00;
  border: 1px solid #333;
  padding: 6px 12px;
  font-family: 'Courier New', monospace;
}

.token-info {
  display: flex;
  align-items: center;
  gap: 4px;

  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 3px;

  font-family: 'Courier New', monospace;
}

.token-label {
  color: #666;
}

.token-count {
  color: #00ff00;
  font-weight: bold;
}

.token-separator {
  color: #333;
}

.token-max {
  color: #999;
}

.refresh-btn,
.debug-btn {
  background: #222;
  color: #00ff00;
  border: 1px solid #333;
  cursor: pointer;

  font-family: 'Courier New', monospace;
  transition: all 0.2s;
}

.refresh-btn:hover,
.debug-btn:hover {
  background: #2a2a2a;
  border-color: #00ff00;
}

.debug-controls {
  display: flex;
  gap: 8px;
}

/* Debug Console */
.debug-console {
  width: 100%;
  height: 300px;
  background: #0a0a0a;
  border-top: 2px solid #00ff00;
  display: flex;
  flex-direction: column;
  margin-top: 8px;
}

.debug-console-header {
  padding: 8px 12px;
  background: #1a1a1a;
  border-bottom: 1px solid #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.debug-console-title {
  color: #00ff00;
  font-weight: bold;

  text-transform: uppercase;
}

.debug-clear-btn {
  background: #ff0000;
  color: #fff;
  border: none;

  cursor: pointer;

  font-weight: bold;
}

.debug-console-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.debug-log-entry {
  margin-bottom: 4px;
  border: 1px solid #333;
  background: #0f0f0f;
  cursor: pointer;
  transition: all 0.2s;
}

.debug-log-entry:hover {
  border-color: #00ff00;
}

.debug-log-entry.expanded {
  border-color: #00ff00;
  background: #1a1a1a;
}

.debug-log-header {
  padding: 6px 8px;
  display: flex;
  gap: 12px;
  align-items: center;
}

.debug-log-time {
  color: #666;
  font-size: 8px;
  min-width: 80px;
}

.debug-log-type {
  color: #00ff00;
  font-size: 8px;
  font-weight: bold;
  text-transform: uppercase;
  min-width: 100px;
}

.debug-log-summary {
  color: #999;

  flex: 1;
}

.debug-log-details {
  padding: 8px;
  border-top: 1px solid #333;
  background: #0a0a0a;
}

.debug-call,
.debug-result,
.debug-other {
  margin-bottom: 8px;
}

.debug-call-label,
.debug-result-label {
  color: #00ff00;

  font-weight: bold;
  margin-bottom: 4px;
}

.debug-call pre,
.debug-result pre,
.debug-other pre {
  color: #999;
  font-size: 8px;
  margin: 0;
  overflow-x: auto;
}

.debug-log-empty {
  color: #666;
  font-style: italic;
  text-align: center;
  padding: 32px;
}

.log-type-init { border-left: 3px solid #00ff00; }
.log-type-introspect { border-left: 3px solid #0099ff; }
.log-type-conversation { border-left: 3px solid #ff00ff; }
.log-type-prompt { border-left: 3px solid #ffff00; }
.log-type-ollama { border-left: 3px solid #ff9900; }
.log-type-response { border-left: 3px solid #00ffff; }
.log-type-error { border-left: 3px solid #ff0000; }
.log-type-choice { border-left: 3px solid #00ff99; }
.log-type-helper { border-left: 3px solid #9900ff; }
.log-type-converse { border-left: 3px solid #ff9900; }

/* Phase indicator */
.phase-indicator {
  padding: 8px;
  margin-bottom: 12px;
  text-align: center;
}

.phase-badge {
  display: inline-block;

  background: #00ff00;
  color: #000;
  font-weight: bold;

  border-radius: 4px;
}

/* Options area */
.options-area {
  margin: 16px 0;
}

.options-header {

  background: #00ff00;
  color: #000;
  font-weight: bold;
  text-transform: uppercase;

  margin-bottom: 12px;
}

.option-button {
  display: block;
  width: 100%;

  margin-bottom: 8px;
  background: #1a1a1a;
  border: 2px solid #333;
  color: #00ff00;
  cursor: pointer;
  font-family: 'Courier New', monospace;

  text-align: left;
  transition: all 0.2s;
}

.option-button:hover {
  background: #222;
  border-color: #00ff00;
}

/* Page preview */
.preview-page {
  padding: 16px;
  background: #0f0f0f;
  border: 1px solid #333;
}

.page-structure {
  color: #00ff00;

  font-family: 'Courier New', monospace;
}

.page-structure > div {
  margin-bottom: 8px;
}

.component-preview-item {

  margin: 4px 0;
  background: #1a1a1a;
  border-left: 2px solid #00ff00;
  color: #999;

}

/* Scrollbar styling */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #0a0a0a;
}

::-webkit-scrollbar-thumb {
  background: #333;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #444;
}

/* Enhanced prompt history styles */
.history-response {
  margin: 4px 0;
  padding: 4px 8px;
  background: #1a1a1a;
  border-radius: 4px;
  border-left: 2px solid #00ff00;
}

.response-intent {
  color: #00ff00;
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 2px;
}

.response-components {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.component-tag {
  background: #004400;
  color: #00ff00;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
}

/* Preview component styles */
.preview-component {
  border-left-color: #00ff00;
}

.preview-function {
  border-left-color: #ffaa00;
}

.preview-action {
  border-left-color: #ff6600;
}
</style>

